<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'

  const props = defineProps<{
    visible: boolean
    analysisConfigSchemas: Array<AnalysisConfigSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'analysis-config-selected': [cfg: AnalysisConfigSchema]
  }>()

  const availableConfigs = ref<AnalysisConfigSchema[]>([])
  const filteredConfigs = ref<AnalysisConfigSchema[]>([])
  const showHiddenConfigs = ref<boolean>(false)
  const loadingConfigs = ref<boolean>(false)
  const hasErrorLoadingConfigs = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group configs by category
  const groupedConfigs = ref<Record<string, AnalysisConfigSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredConfigs.value = availableConfigs.value.filter((schema: AnalysisConfigSchema) => {
        return (
          (schema.description && schema.description.toLowerCase().includes(search)) ||
          (schema.display_name && schema.display_name.toLowerCase().includes(search))
        )
      })
    } else {
      filteredConfigs.value = [...availableConfigs.value]
    }
    groupConfigsByCategory()
  }

  const groupConfigsByCategory = () => {
    const grouped: Record<string, AnalysisConfigSchema[]> = {}
    filteredConfigs.value.forEach((cfg) => {
      if (!grouped[cfg.category]) {
        grouped[cfg.category] = []
      }
      grouped[cfg.category].push(cfg)
    })
    groupedConfigs.value = grouped
  }

  const selectConfig = (cfg: AnalysisConfigSchema) => {
    emit('analysis-config-selected', cfg)
    // Keep dialog open to allow adding multiple items easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableConfigs()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableConfigs()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableConfigs = () => {
    availableConfigs.value = props.analysisConfigSchemas
    filteredConfigs.value = props.analysisConfigSchemas
    groupConfigsByCategory()
  }

  // Watch for visibility changes to load configs if needed
  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.analysisConfigSchemas,
    (newItems) => {
      availableConfigs.value = [...newItems]
      filteredConfigs.value = [...newItems]
      groupConfigsByCategory()
    }
  )

  defineExpose({
    availableConfigs
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Analysis Configuration"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="analysis-config-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search analysis configurations by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox class="ml-2 flex-shrink-0 whitespace-nowrap" v-model:checked="showHiddenConfigs">
          Show All Configs
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingConfigs" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available configurations...</p>
      </div>

      <!-- Configs List -->
      <div v-else id="configsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingConfigs"
          v-for="(configs, category) in groupedConfigs"
          :key="category"
          class="mb-6"
        >
          <template
            v-if="configs.filter((x) => (showHiddenConfigs ? x : x.is_visible === true)).length > 0"
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ configs.length }} {{ configs.length === 1 ? 'config' : 'configs' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="cfg in configs.filter((x) =>
                  showHiddenConfigs ? x : x.is_visible === true
                )"
                :key="cfg.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectConfig(cfg)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ cfg.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">{{ cfg.description }}</p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ cfg.fields.length }} field{{ cfg.fields.length !== 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingConfigs"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading analysis configurations.
        </div>
        <!-- No Results -->
        <div
          v-if="!hasErrorLoadingConfigs && filteredConfigs.length === 0"
          class="py-8 text-center"
        >
          <p>
            {{
              searchText.trim()
                ? 'No configurations found matching your search.'
                : 'No configurations available.'
            }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs text-gray-400">
            Try adjusting your search terms or clear the search to browse all configurations.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any configuration above to add it to your pipeline</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .analysis-config-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #configsListContainer {
    flex: 1;
  }
  #configsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #configsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #configsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #configsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
